import React, {useCallback, useMemo, useState} from 'react';
import {
    Dimensions,
    Platform,
    RefreshControl,
    ScrollView,
    StyleSheet,
    Text,
    TouchableOpacity,
    View,
} from 'react-native';
import {useBottomTabBarHeight} from '@react-navigation/bottom-tabs';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTranslation} from 'react-i18next';
import {useNavigation} from '@react-navigation/native';
import { usePremiumLegacyTheme } from '../helpers/modeScreenTheme';
import { PillTabs } from '../components/premium';
import {useWorkSheets} from '../helpers/WorkSheetContext';
import {statusColor, ServiceEmptyState} from '../components/ServiceUI';
import {useTopInset} from '../helpers/safeArea';
import {useServiceMode} from '../helpers/ServiceModeContext';

const { width } = Dimensions.get('window');
const CELL_SIZE = Math.floor((width - 48) / 7);
const HOURS = Array.from({ length: 12 }, (_, i) => i + 7); // 7:00 - 18:00

const DAYS_HU = ['H', 'K', 'Sze', 'Cs', 'P', 'Szo', 'V'];
const VIEW_MODES = ['daily', 'weekly', 'monthly'];

// Format a Date object to 'YYYY-MM-DD' string
const formatDateKey = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

// Get an array of 7 Date objects for the week containing the given date (Mon-Sun)
const getWeekDates = (date) => {
    const d = new Date(date);
    const day = d.getDay(); // 0=Sun, 1=Mon, ...
    const diff = day === 0 ? -6 : 1 - day; // Adjust to Monday start
    d.setDate(d.getDate() + diff);
    return Array.from({ length: 7 }, (_, i) => {
        const wd = new Date(d);
        wd.setDate(d.getDate() + i);
        return wd;
    });
};

// Get all day cells for a month calendar grid (including padding from prev/next month)
const getMonthDays = (year, month) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDay = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1; // Monday-based
    const days = [];

    // Previous month padding
    for (let i = startDay - 1; i >= 0; i--) {
        const d = new Date(year, month, -i);
        days.push({ date: d, isCurrentMonth: false });
    }

    // Current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
        days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }

    // Next month padding (fill to complete the last row)
    const remaining = 7 - (days.length % 7);
    if (remaining < 7) {
        for (let i = 1; i <= remaining; i++) {
            days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
        }
    }

    return days;
};

const RepairmanCalendarScreen = () => {
    const { t } = useTranslation();
    const { theme } = usePremiumLegacyTheme();
    const navigation = useNavigation();
    const { worksheets, fetchWorksheets } = useWorkSheets();
    const tabBarHeight = useBottomTabBarHeight();
    const topInset = useTopInset();
    const { isFeatureEnabled } = useServiceMode();

    const [viewMode, setViewMode] = useState('daily');
    const [currentDate, setCurrentDate] = useState(new Date());
    const [refreshing, setRefreshing] = useState(false);

    const dateKey = formatDateKey(currentDate);
    const todayKey = formatDateKey(new Date());

    const onRefresh = useCallback(async () => {
        setRefreshing(true);
        try { await fetchWorksheets(); } finally { setRefreshing(false); }
    }, [fetchWorksheets]);

    const eventsByDate = useMemo(() => {
        const map = {};
        (worksheets || []).forEach(ws => {
            const sourceDate = ws.scheduledAt || ws.dueDate || ws.createdAt;
            if (!sourceDate) return;
            const d = new Date(sourceDate);
            if (Number.isNaN(d.getTime())) return;
            const dateStr = formatDateKey(d);
            const startHour = ws.scheduledAt ? d.getHours() || 9 : 9;

            if (!map[dateStr]) map[dateStr] = [];
            map[dateStr].push({
                _id: ws._id,
                title: ws.jobSummary || ws.worksheetId,
                customer: ws.customer || t('repair_unknown_customer') || '—',
                car: ws.car || '',
                plate: ws.plate || '',
                date: dateStr,
                startHour,
                duration: Number(ws.estimatedHours) || 1,
                status: ws.status,
                color: statusColor(ws.status),
                worksheet: ws,
            });
        });
        // Sort by start hour per day
        Object.keys(map).forEach(k => map[k].sort((a, b) => a.startHour - b.startHour));
        return map;
    }, [worksheets, t]);

    const navigateDate = useCallback((direction) => {
        setCurrentDate(prev => {
            const d = new Date(prev);
            if (viewMode === 'daily') d.setDate(d.getDate() + direction);
            else if (viewMode === 'weekly') d.setDate(d.getDate() + (7 * direction));
            else d.setMonth(d.getMonth() + direction);
            return d;
        });
    }, [viewMode]);

    const goToToday = useCallback(() => {
        setCurrentDate(new Date());
    }, []);

    const handleEventPress = useCallback((event) => {
        // Use the full worksheet object if available, otherwise fallback (though we mapped it)
        const wsToPass = event.worksheet || {
            _id: event._id,
            worksheetId: `ML-${event._id}`,
            customer: event.customer,
            car: event.car || '',
            plate: event.plate || '',
            jobSummary: event.title,
            status: event.status,
            createdAt: event.date,
        };

        navigation.navigate('WorkSheetDetailScreen', {
            worksheet: wsToPass,
            worksheetId: wsToPass._id
        });
    }, [navigation]);

    const getViewModeLabel = (mode) => {
        const labels = { daily: t('repair_daily'), weekly: t('repair_weekly'), monthly: t('repair_monthly') };
        return labels[mode];
    };

    // Dynamic styles based on theme
    const colors = theme.colors;

    // Daily View
    const renderDailyView = () => {
        const todayEvents = eventsByDate[dateKey] || [];
        return (
            <ScrollView
                style={styles.dailyContainer}
                showsVerticalScrollIndicator={false}
                refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={colors.primary} colors={[colors.primary]} />}
            >
                {HOURS.map(hour => {
                    const hourEvents = todayEvents.filter(e => e.startHour === hour);
                    return (
                        <View key={hour} style={styles.hourRow}>
                            <Text style={[styles.hourLabel, { color: colors.subText }]}>{`${String(hour).padStart(2, '0')}:00`}</Text>
                            <View style={[styles.hourContent, { borderLeftColor: colors.border }]}>
                                {hourEvents.length > 0 ? hourEvents.map(event => (
                                    <TouchableOpacity
                                        key={event._id}
                                        style={[styles.eventCard, {
                                            backgroundColor: event.color + '22',
                                            borderLeftColor: event.color,
                                            minHeight: Math.max(50, event.duration * 50),
                                        }]}
                                        onPress={() => handleEventPress(event)}
                                        activeOpacity={0.7}
                                    >
                                        <Text style={[styles.eventTitle, { color: event.color }]}>{event.title}</Text>
                                        <Text style={[styles.eventDetail, { color: colors.subText }]}>{event.customer} • {event.car}</Text>
                                        <View style={styles.eventMeta}>
                                            <Text style={[styles.eventTime, { color: colors.subText }]}>{event.duration}h</Text>
                                            <View style={[styles.eventPlate, { backgroundColor: colors.card }]}>
                                                <Text style={[styles.eventPlateText, { color: colors.subText }]}>{event.plate}</Text>
                                            </View>
                                        </View>
                                    </TouchableOpacity>
                                )) : (
                                    <View style={styles.emptyHour} />
                                )}
                            </View>
                        </View>
                    );
                })}
                <View style={{ height: 100 }} />
            </ScrollView>
        );
    };

    // Weekly View
    const renderWeeklyView = () => {
        const weekDates = getWeekDates(currentDate);
        return (
            <ScrollView style={styles.weeklyContainer} showsVerticalScrollIndicator={false}>
                {/* Day headers */}
                <View style={styles.weekHeader}>
                    {weekDates.map((date, i) => {
                        const key = formatDateKey(date);
                        const isToday = key === todayKey;
                        return (
                            <TouchableOpacity
                                key={i}
                                style={[
                                    styles.weekDayHeader,
                                    { backgroundColor: colors.card, borderColor: colors.border },
                                    isToday && { backgroundColor: colors.primary, borderColor: colors.primary },
                                ]}
                                onPress={() => { setCurrentDate(date); setViewMode('daily'); }}
                            >
                                <Text style={[styles.weekDayName, { color: colors.subText }, isToday && { color: '#fff' }]}>
                                    {DAYS_HU[i]}
                                </Text>
                                <Text style={[styles.weekDayNum, { color: colors.text }, isToday && { color: '#fff' }]}>
                                    {date.getDate()}
                                </Text>
                            </TouchableOpacity>
                        );
                    })}
                </View>
                {/* Events per day */}
                <View style={styles.weekBody}>
                    {weekDates.map((date, i) => {
                        const key = formatDateKey(date);
                        const dayEvents = eventsByDate[key] || [];
                        return (
                            <View key={i} style={styles.weekDayColumn}>
                                {dayEvents.map(event => (
                                    <TouchableOpacity
                                        key={event._id}
                                        style={[styles.weekEvent, { borderLeftColor: event.color, backgroundColor: event.color + '18' }]}
                                        onPress={() => handleEventPress(event)}
                                        activeOpacity={0.7}
                                    >
                                        <Text style={[styles.weekEventTime, { color: colors.subText }]}>{event.startHour}:00</Text>
                                        <Text style={[styles.weekEventTitle, { color: colors.text }]} numberOfLines={1}>{event.title}</Text>
                                        <Text style={[styles.weekEventCustomer, { color: colors.subText }]} numberOfLines={1}>{event.customer}</Text>
                                    </TouchableOpacity>
                                ))}
                                {dayEvents.length === 0 && (
                                    <View style={styles.weekEmptyDay}>
                                        <Icon name="minus" size={14} color={colors.subText} />
                                    </View>
                                )}
                            </View>
                        );
                    })}
                </View>
            </ScrollView>
        );
    };

    // Monthly View
    const renderMonthlyView = () => {
        const monthDays = getMonthDays(currentDate.getFullYear(), currentDate.getMonth());
        return (
            <ScrollView style={styles.monthlyContainer} showsVerticalScrollIndicator={false}>
                {/* Day name headers */}
                <View style={styles.monthDayHeaders}>
                    {DAYS_HU.map((d, i) => (
                        <View key={i} style={styles.monthDayHeaderCell}>
                            <Text style={[styles.monthDayHeaderText, { color: colors.subText }]}>{d}</Text>
                        </View>
                    ))}
                </View>
                {/* Calendar grid */}
                <View style={styles.monthGrid}>
                    {monthDays.map((item, i) => {
                        const key = formatDateKey(item.date);
                        const dayEvents = eventsByDate[key] || [];
                        const isToday = key === todayKey;
                        const isSelected = key === dateKey;
                        return (
                            <TouchableOpacity
                                key={i}
                                style={[
                                    styles.monthCell,
                                    !item.isCurrentMonth && styles.monthCellInactive,
                                    isToday && !isSelected && { backgroundColor: colors.primary + '1F' },
                                    isSelected && { backgroundColor: colors.primary },
                                ]}
                                onPress={() => { setCurrentDate(item.date); setViewMode('daily'); }}
                                activeOpacity={0.7}
                            >
                                <Text style={[
                                    styles.monthCellDay,
                                    { color: colors.text },
                                    !item.isCurrentMonth && { color: colors.subText },
                                    isToday && { color: colors.primary, fontWeight: '800' },
                                    isSelected && { color: '#FFFFFF', fontWeight: '800' },
                                ]}>
                                    {item.date.getDate()}
                                </Text>
                                {dayEvents.length > 0 && (
                                    <View style={styles.monthDots}>
                                        {dayEvents.slice(0, 3).map((e, j) => (
                                            <View key={j} style={[styles.dot, { backgroundColor: isSelected ? '#FFFFFF' : e.color }]} />
                                        ))}
                                    </View>
                                )}
                                {dayEvents.length > 0 && (
                                    <Text style={[styles.monthEventCount, { color: isSelected ? '#FFFFFF' : colors.subText }]}>{dayEvents.length}</Text>
                                )}
                            </TouchableOpacity>
                        );
                    })}
                </View>

                {/* Events for selected date */}
                <View style={styles.monthEventsList}>
                    <Text style={[styles.monthEventsTitle, { color: colors.text }]}>
                        {currentDate.toLocaleDateString('hu-HU', { month: 'long', day: 'numeric' })}
                    </Text>
                    {(eventsByDate[dateKey] || []).map(event => (
                        <TouchableOpacity
                            key={event._id}
                            style={[styles.monthEventCard, {
                                borderLeftColor: event.color,
                                backgroundColor: event.color + '14',
                            }]}
                            onPress={() => handleEventPress(event)}
                        >
                            <Text style={[styles.monthEventTime, { color: colors.subText }]}>{event.startHour}:00 - {event.startHour + event.duration}:00</Text>
                            <Text style={[styles.monthEventName, { color: colors.text }]}>{event.title}</Text>
                            <Text style={[styles.monthEventCustomerText, { color: colors.subText }]}>{event.customer} • {event.car}</Text>
                        </TouchableOpacity>
                    ))}
                    {(!eventsByDate[dateKey] || eventsByDate[dateKey].length === 0) && (
                        <Text style={[styles.noEventsText, { color: colors.subText }]}>{t('repair_no_scheduled_jobs')}</Text>
                    )}
                </View>
                <View style={{ height: 20 }} />
            </ScrollView>
        );
    };

    const getDateTitle = () => {
        if (viewMode === 'daily') {
            return currentDate.toLocaleDateString('hu-HU', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
        }
        if (viewMode === 'weekly') {
            const week = getWeekDates(currentDate);
            return `${week[0].toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' })} - ${week[6].toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' })}`;
        }
        return currentDate.toLocaleDateString('hu-HU', { year: 'numeric', month: 'long' });
    };

    if (!isFeatureEnabled('bookings')) {
        return (
            <View style={[styles.container, { backgroundColor: colors.background }]}>
                <View style={{ paddingTop: topInset + 12 }} />
                <ServiceEmptyState
                    icon="calendar-remove-outline"
                    title={t('repair_feature_disabled', { defaultValue: 'Ez a funkció ki van kapcsolva ebben a szerviz módban' })}
                    theme={theme}
                    actionLabel={t('back', { defaultValue: 'Vissza' })}
                    onAction={() => navigation.goBack()}
                />
            </View>
        );
    }

    return (
        <View style={[styles.container, { backgroundColor: colors.background }]}>
            {/* Header */}
            <View style={[styles.header, { paddingTop: topInset + 12 }]}>
                <Text style={[styles.headerTitle, { color: colors.text }]}>{t('repair_calendar')}</Text>
                <TouchableOpacity style={[styles.todayBtn, {
                    backgroundColor: colors.primary + '1F',
                    borderColor: colors.primary + '40',
                }]} onPress={goToToday}>
                    <Text style={[styles.todayBtnText, { color: colors.primary }]}>{t('repair_today')}</Text>
                </TouchableOpacity>
            </View>

            {/* View Mode Selector */}
            <View style={styles.viewModeRow}>
                <PillTabs
                    variant="solid"
                    options={VIEW_MODES.map(mode => ({ value: mode, label: getViewModeLabel(mode) }))}
                    value={viewMode}
                    onChange={setViewMode}
                />
            </View>

            {/* Date Navigation */}
            <View style={styles.dateNav}>
                <TouchableOpacity onPress={() => navigateDate(-1)} style={[styles.dateNavBtn, {
                    backgroundColor: colors.card,
                    borderColor: colors.border,
                }]}>
                    <Icon name="chevron-left" size={24} color={colors.text} />
                </TouchableOpacity>
                <Text style={[styles.dateNavTitle, { color: colors.text }]}>{getDateTitle()}</Text>
                <TouchableOpacity onPress={() => navigateDate(1)} style={[styles.dateNavBtn, {
                    backgroundColor: colors.card,
                    borderColor: colors.border,
                }]}>
                    <Icon name="chevron-right" size={24} color={colors.text} />
                </TouchableOpacity>
            </View>

            {/* Calendar Content */}
            {viewMode === 'daily' && renderDailyView()}
            {viewMode === 'weekly' && renderWeeklyView()}
            {viewMode === 'monthly' && renderMonthlyView()}

            {/* FAB */}
            <TouchableOpacity
                style={[styles.fab, { bottom: tabBarHeight + 20 }]}
                onPress={() => navigation.navigate('WorkSheetDetailScreen', { isNew: true })}
                activeOpacity={0.8}
            >
                <View style={[styles.fabCircle, { backgroundColor: colors.primary }]}>
                    <Icon name="plus" size={28} color="#fff" />
                </View>
            </TouchableOpacity>
        </View>
    );
};

const styles = StyleSheet.create({
    container: { flex: 1 },
    header: {
        flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
        paddingHorizontal: 16, paddingBottom: 8, // paddingTop applied dynamically via useTopInset()
    },
    headerTitle: { fontSize: 30, fontWeight: '800', letterSpacing: -0.8 },
    todayBtn: {
        paddingHorizontal: 16, paddingVertical: 8, borderRadius: 999,
        borderWidth: 1,
    },
    todayBtnText: { fontSize: 13, fontWeight: '600' },
    viewModeRow: { marginHorizontal: 12, marginVertical: 8 },
    dateNav: {
        flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
        paddingHorizontal: 16, paddingVertical: 8,
    },
    dateNavBtn: {
        width: 40, height: 40, borderRadius: 20, borderWidth: 1,
        alignItems: 'center', justifyContent: 'center',
    },
    dateNavTitle: { fontSize: 15, fontWeight: '600', textAlign: 'center', flex: 1 },

    // Daily
    dailyContainer: { flex: 1, paddingHorizontal: 16 },
    hourRow: { flexDirection: 'row', minHeight: 60, marginBottom: 2 },
    hourLabel: { width: 50, fontSize: 12, fontWeight: '600', paddingTop: 4 },
    hourContent: {
        flex: 1, borderLeftWidth: 1,
        paddingLeft: 12, minHeight: 50,
    },
    eventCard: {
        borderLeftWidth: 3, borderRadius: 18, padding: 12, marginBottom: 4,
    },
    eventTitle: { fontSize: 14, fontWeight: '700' },
    eventDetail: { fontSize: 12, marginTop: 2 },
    eventMeta: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 4 },
    eventTime: { fontSize: 11, fontWeight: '600' },
    eventPlate: {
        paddingHorizontal: 8, paddingVertical: 2, borderRadius: 999,
    },
    eventPlateText: { fontSize: 10, fontWeight: '700', letterSpacing: 0.5 },
    emptyHour: { minHeight: 30 },

    // Weekly
    weeklyContainer: { flex: 1, paddingHorizontal: 8 },
    weekHeader: { flexDirection: 'row', gap: 4, marginBottom: 8 },
    weekDayHeader: {
        flex: 1, alignItems: 'center', paddingVertical: 8, borderRadius: 999, borderWidth: 1,
    },
    weekDayName: { fontSize: 11, fontWeight: '600' },
    weekDayNum: { fontSize: 16, fontWeight: '700', marginTop: 2 },
    weekBody: { flexDirection: 'row', gap: 4 },
    weekDayColumn: { flex: 1 },
    weekEvent: {
        borderLeftWidth: 3, borderRadius: 14, padding: 6, marginBottom: 4,
    },
    weekEventTime: { fontSize: 10, fontWeight: '600' },
    weekEventTitle: { fontSize: 11, fontWeight: '600', marginTop: 2 },
    weekEventCustomer: { fontSize: 10, marginTop: 1 },
    weekEmptyDay: { height: 40, alignItems: 'center', justifyContent: 'center' },

    // Monthly
    monthlyContainer: { flex: 1, paddingHorizontal: 16 },
    monthDayHeaders: { flexDirection: 'row', marginBottom: 4 },
    monthDayHeaderCell: { width: CELL_SIZE, alignItems: 'center', paddingVertical: 6 },
    monthDayHeaderText: { fontSize: 12, fontWeight: '600' },
    monthGrid: { flexDirection: 'row', flexWrap: 'wrap' },
    monthCell: {
        width: CELL_SIZE, height: CELL_SIZE, alignItems: 'center',
        justifyContent: 'center', borderRadius: CELL_SIZE / 2,
    },
    monthCellInactive: { opacity: 0.3 },
    monthCellDay: { fontSize: 14, fontWeight: '500' },
    monthDots: { flexDirection: 'row', gap: 3, marginTop: 3 },
    dot: { width: 5, height: 5, borderRadius: 2.5 },
    monthEventCount: { fontSize: 9, marginTop: 1 },
    monthEventsList: { marginTop: 16, paddingBottom: 100 },
    monthEventsTitle: { fontSize: 16, fontWeight: '700', marginBottom: 10 },
    monthEventCard: {
        borderLeftWidth: 3, borderRadius: 18, padding: 14, marginBottom: 8,
    },
    monthEventTime: { fontSize: 12, fontWeight: '600' },
    monthEventName: { fontSize: 15, fontWeight: '600', marginTop: 4 },
    monthEventCustomerText: { fontSize: 13, marginTop: 2 },
    noEventsText: { fontSize: 14, textAlign: 'center', paddingVertical: 20 },

    // FAB
    fab: { position: 'absolute', bottom: 24, right: 20 },
    fabCircle: {
        width: 56, height: 56, borderRadius: 28,
        alignItems: 'center', justifyContent: 'center',
        ...Platform.select({
            ios: { shadowColor: '#EA580C', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.4, shadowRadius: 8 },
            android: { elevation: 8 },
        }),
    },
});

export default RepairmanCalendarScreen;
